import { useMemo } from 'react';
import type { ComponentPropsWithoutRef, FC } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { isHiddenDomain } from 'mastodon/utils/handle';

import { Skeleton } from '../skeleton';

import type { DisplayNameProps } from './index';
import { DisplayNameWithoutDomain } from './no-domain';

const messages = defineMessages({
  invalidHandle: {
    id: 'account.hame.invalid_handle',
    defaultMessage: 'Handle unavailable',
  },
});

export function useAccountHandle(
  account: DisplayNameProps['account'],
  localDomain: DisplayNameProps['localDomain'],
) {
  const intl = useIntl();

  return useMemo(() => {
    if (!account) {
      return null;
    }

    if (account.invalid_handle)
      return intl.formatMessage(messages.invalidHandle);

    let acct = account.acct;

    if (!acct.includes('@') && localDomain) {
      acct = `${acct}@${localDomain}`;
    }

    const [username, domain] = acct.split('@');
    if (isHiddenDomain(domain)) {
      acct = username ?? acct;
    }

    return `@${acct}`;
  }, [account, localDomain, intl]);
}

export const DisplayNameDefault: FC<
  Omit<DisplayNameProps, 'variant'> & ComponentPropsWithoutRef<'span'>
> = ({ account, localDomain, className, ...props }) => {
  const username = useAccountHandle(account, localDomain);

  // A group's auto-created feed-posting bot has no visitable profile (see
  // Account#group_bot? / Api::V1::Accounts::BaseController) -- its @handle
  // would just be a dead end, so it's never shown, not even as a skeleton.
  if (account?.group_bot) {
    return (
      <DisplayNameWithoutDomain
        account={account}
        className={className}
        {...props}
      />
    );
  }

  return (
    <DisplayNameWithoutDomain
      account={account}
      className={className}
      {...props}
    >
      {' '}
      <span className='display-name__account'>
        {username ?? <Skeleton width='7ch' />}
      </span>
    </DisplayNameWithoutDomain>
  );
};
